import { usePreviewRunServicesQuery } from "@/api/previewInsights";
import type { LabRunModel } from "@/lib/labRunModel";
import {
  callsForOrdinal,
  concernTone,
  safetyCallOf,
  safetyCategories,
} from "@/lib/labServices";
import {
  SAFETY_MECHANISM_LABELS,
  safetyOutcomeLabel,
  safetyOutcomeTone,
} from "@/lib/previewDecisions";
import { cn } from "@/lib/cn";
import { Badge } from "@/ui/Badge";

/** Whole-conversation Safety: one row per patient message, matched to the services read by its position in the run. */
export function LabSafetyRunTab({
  projectId,
  model,
  live,
  selectedTurn,
  onSelectTurn,
}: {
  projectId: string;
  model: LabRunModel;
  live: boolean;
  selectedTurn: number | null;
  onSelectTurn: (turn: number) => void;
}) {
  const services = usePreviewRunServicesQuery(projectId, model.previewRunRef, {
    live,
  });
  const turns = model.lab.turns;
  if (turns.length === 0) {
    return <p className="text-sm text-muted-foreground">No messages yet.</p>;
  }
  return (
    <ol className="space-y-2">
      {turns.map((turn, index) => {
        const call = safetyCallOf(callsForOrdinal(services.data, index + 1));
        const categories = call ? safetyCategories(call) : [];
        return (
          <li key={turn.turnNumber}>
            <button
              type="button"
              aria-current={turn.turnNumber === selectedTurn ? "true" : undefined}
              onClick={() => onSelectTurn(turn.turnNumber)}
              className={cn(
                "w-full space-y-1.5 rounded-md border p-3 text-start text-sm focus-visible:outline-2 focus-visible:outline-ring",
                turn.turnNumber === selectedTurn
                  ? "border-primary bg-secondary"
                  : "border-border bg-card hover:bg-secondary/50",
              )}
            >
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-medium">
                  Message <span dir="ltr">{index + 1}</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  Turn <span dir="ltr">{turn.turnNumber}</span>
                </span>
                {call ? (
                  <>
                    <Badge tone={concernTone(call.outcome)}>
                      {`Concern: ${call.outcome}`}
                    </Badge>
                    {categories.map((category) => (
                      <Badge key={category} tone="outline">
                        <span dir="ltr">{category}</span>
                      </Badge>
                    ))}
                    {call.result["alertSuppressed"] === true ? (
                      <Badge tone="outline">alert suppressed</Badge>
                    ) : null}
                    <span className="ms-auto text-xs text-muted-foreground">
                      <span dir="ltr">{call.mode}</span>
                      {call.latencyMs != null ? (
                        <>
                          {" · "}
                          <span dir="ltr">{call.latencyMs} ms</span>
                        </>
                      ) : null}
                    </span>
                  </>
                ) : (
                  <Badge tone="outline">
                    {services.isPending
                      ? "Loading…"
                      : services.isError
                        ? "Safety result unavailable"
                        : "Safety not run"}
                  </Badge>
                )}
              </span>
              {turn.safety.length > 0 ? (
                <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  Reply checks:
                  {turn.safety.map((event) => (
                    <Badge
                      key={event.eventRef}
                      tone={safetyOutcomeTone(event.detail.outcome)}
                    >
                      {`${
                        SAFETY_MECHANISM_LABELS[
                          event.detail
                            .mechanism as keyof typeof SAFETY_MECHANISM_LABELS
                        ] ?? event.detail.mechanism
                      }: ${safetyOutcomeLabel(event.detail.outcome)}`}
                    </Badge>
                  ))}
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
